<template><div class="card">
  <div>
    <router-link :to="`/u/${unit_id}/s`" target="_blank" v-html="unit_name" />
    <span class="error" v-if="!valid">Not saved. See below:</span>
    <span v-else-if="timeout" class="changed">Changed...</span>
    <span v-else-if="remote && ref" class="saving">Saving... <Loading /></span>
    <span v-else-if="saved" class="saved">Saved</span>
    <button v-if="err !== null" class="error" @click="touch(true)">
      An error occurred. Click here to retry.
    </button>
  </div>
  <div v-if="!timezones || !remote" class="cover">
    <Loading v-if="!timezones || ref" />
    <button v-else @click="initialize" class="error">
      An error occurred. Click here to retry.
    </button>
  </div>
  <template>
    <label>
      Heartbeat period
      <input class="period" v-model="local.interval" :class="{error:!validations.interval}" placeholder="disabled" />
      (minutes)
    </label>
    <div>
      <span>
        Downloads
      </span>
      <label>
        <input type="checkbox" v-model="local.tfl" />
        Fault Log
      </label>
      <label>
        <input type="checkbox" v-model="local.tdr" />
        Recorder Log
      </label>
    </div>
    <div>
      <label>
        Log Time
        <input class="time" v-model="local.tod" :class="{error:!validations.tod}" placeholder="anytime" />
      </label>
      <label>
        Timezone
        <suggestions :items="filteredTimezones" v-model="local.tz" :class="{error:!validations.tz}" />
      </label>
    </div>
  </template>
</div></template>
<script>
import api from '../../../api'
import Loading from '../../Loading'
import moment from 'moment'
import Suggestions from '../../Suggestions'

const validateNatural = (txt, nullable) => {
  txt = txt.toString().trim()
  if (!txt.length) {
    if (nullable) return null
    else throw new Error('Value cannot be null.')
  }
  if (!/[0-9]+/.test(txt))
    throw new Error('Value is not a natural number.')
  return parseInt(txt, 10)
}

const validateTime = (txt, nullable) => {
  txt = txt.toString().trim()
  if (!txt.length) {
    if (nullable) return null
    else throw new Error('Value cannot be null.')
  }
  const t = moment(txt, ['h:m a', 'H:m'])
  if (!t.isValid())
    throw new Error('Invalid date string')
  return [t.hour(), t.minute()]
}

const formatInterval = (interval) => (interval > 0 ? `${interval}` : '')
const formatTime = (tod) => (tod ? moment().hours(tod[0]).minutes(tod[1]).format('H:mm') : '')

export default {
  name: 'schedule-card',
  props: ['unit_id','unit_name'],
  components: { Loading, Suggestions },
  data() {
    return {
      ref: null,
      remote: null,
      local: { interval: null, tfl: null, tdr: null, tod: null, tz: null },
      timeout: null,
      validations: { interval: true, tod: true, tz: true },
      parsed: { interval: null, tod: null, tz: null },
      err: null,
      saved: false,
    }
  },
  computed: {
    timezones() { return this.$store.state.timezones },
    timezonesLC() { return this.timezones.map(tz => tz.toLowerCase()) },
    valid() {
      for (const k in this.validations)
        if (!this.validations[k])
          return false
      return true
    },
    filteredTimezones() {
      let zz = this.timezones
      for (let token of (this.local.tz || '').toLowerCase().split(' ')) {
        zz = zz.filter(tz => (tz.toLowerCase().indexOf(token) >= 0))
      }
      return zz
    },
  },
  created() {
    this.initialize()
  },
  methods: {
    async initialize() {
      if (this.ref || this.remote) return
      let ref = this.ref = { uid: this.unit_id }
      try {
        const o = await api.schedule(ref)
        if (this.ref === ref) {
          this.local = {
            interval: formatInterval(o.interval),
            tfl: o.tfl,
            tdr: o.tdr,
            tod: formatTime(o.tod),
            tz: o.tz,
          }
          this.remote = o
          this.validations = { interval: true, tod: true, tz: true }
        }
      } finally {
        this.ref = null
      }
    },
    validateTimezone(txt) {
      const i = this.timezonesLC.indexOf((txt || '').trim().toLowerCase())
      if (i >= 0) return this.timezones[i]
      throw new Error('Unrecognized timezone.')
    },
    touch(immediate) {
      this.saved = false
      if (this.ref) {
        this.ref.cancel()
        this.ref = null
      }
      if (this.timeout !== null) {
        clearTimeout(this.timeout)
        this.timeout = null
      }
      if (this.valid) {
        if (immediate)
          this.saveNow()
        else
          this.timeout = setTimeout(() => { this.saveNow() }, 500)
      }
    },
    async saveNow() {
      this.timeout = null
      this.err = null
      let ref = this.ref = {
        uid: this.unit_id,
        data: {
          tfl: this.local.tfl,
          tdr: this.local.tdr,
          ...this.parsed,
        },
      }
      try {
        const o = await api.schedule(ref)
        if (this.ref === ref) {
          this.saved = true
          this.ref = null
          this.remote = o
        }
      } catch(e) {
        if (this.ref === ref) {
          console.error(e)
          this.err = true
          this.ref = null
        }
      }
    },
  },

  watch: {
    'local.interval': function(v, ov) {
      v = v.trim()
      if (v !== '' && v.replace(/0/g, '') === '') {
        this.local.interval = ''
        return
      }
      if (v === '') {
        this.parsed.interval = 0
        this.validations.interval = true
      } else if (/^[0-9]+$/.test(v)) {
        this.parsed.interval = parseInt(v, 10)
        this.validations.interval = true
      } else {
        this.parsed.interval = null
        this.validations.interval = false
      }
      if (ov !== null)
        this.touch()
    },
    'local.tod': function(v, ov) {
      try {
        this.parsed.tod = validateTime(v, true)
        this.validations.tod = true
      } catch(e) {
        this.parsed.tod = null
        this.validations.tod = false
      }
      if (ov !== null)
        this.touch()
    },
    'local.tz': function(v, ov) {
      try {
        this.parsed.tz = this.validateTimezone(v)
        this.validations.tz = true
      } catch(e) {
        this.parsed.tz = null
        this.validations.tz = false
      }
      if (ov !== null)
        this.touch()
    },
    'local.tfl': function(nv, ov) { if (ov !== null) this.touch(true) },
    'local.tdr': function(nv, ov) { if (ov !== null) this.touch(true) },
  },
}
</script>
<style lang="scss" scoped>
.card {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 16px;
  background-color: #fff;
  flex-basis: max-content;
  position: relative;
  overflow: visible;

  display: flex;
  flex-direction: column;
  gap: 10px;
  white-space: nowrap;

  a { font-weight: bold; }
  .period { width: 4em; &::placeholder { color: #f88; opacity: 1; } }
  .time { width: 4em; &::placeholder { color: #4a4; opacity: 1; } }
  .tz { width: 8em; }

  div, label { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  label { gap: 5px; }

  .cover {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(200, 200, 200, 0.8);
    .loading {
      width: 60px;
      height: 60px;
    }
  }
  .changed { color: #aaa; }
  .saving { color: #e80; > .loading { width: 15px; height: 15px; vertical-align: top; } }
  .saved { color: #080; }
}
</style>
